/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */
import { CoherenceHeader, IGlobalSearchProps, SearchSettings } from "@coherence-design-system/controls";
import { EnhancedSearch } from "@coherence-design-system/enhanced-search";
import { Template } from "@coherence-design-system/slot";
import {
  IButtonStyles,
  ISearchBox,
  Icon,
  Spinner,
  SpinnerSize,
  Stack,
  mergeStyleSets,
  mergeStyles
} from "@fluentui/react";
import { useBoolean } from "@fluentui/react-hooks";
import { Panel } from "@fluentui/react/lib/Panel";
import AssessmentIcon from "@mui/icons-material/Assessment";
import CategoryIcon from "@mui/icons-material/Category";
import GroupsIcon from "@mui/icons-material/Groups";
import GroupIcon from "@mui/icons-material/Group";
import SearchIcon from "@mui/icons-material/SearchOutlined";
import axios from "axios";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import HeaderHelpBody from "../helper/HeaderHelpBody";
import CxElogo from "../images/CxElogo.png";
import { IAzSearchInput } from "../interfaces/Customer.interfaces";
import PowerBIReportModal from "../MuiComponents/PowerBIReportModal";
import { TopCustomerProps, TopPartnersProps, TopProductProps, TopReportProps } from "../pages/HomePage";
import { getCustomers, getPartners, getProducts, getReports } from "../Services/AzSearchServices";
import {
  useGetRecentlySearchedItemsQuery,
  useInsertRecentlySearchedCustomerMutation,
  useInsertRecentlySearchedProductMutation,
  useInsertRecentlySearchedReportMutation
} from "../slices/Common.slice";
import { useAppSelector } from "../store/hooks";
import { applyAzSearchInputFilter, isNumberOnly, isTenantIdOnly, isValidJson } from "../Utils/js-common";
import FeedbackBody from "./Feedback/FeedBackBody";
import AdminNavigationPanel from "./AdminNavigationPanel";
import { GET_KEY_AND_SEARCH_LIMIT } from "./Home/HomePage.constants";

const overrideStyles = mergeStyleSets({
  searchBar: {
    color: "white",
    display: "flex",
    justifyContent: "space-around",
    fontSize: 20,
    fontWeight: 700,
    position: "absolute",
    left: 50,
    marginTop: "5px",
    cursor: "pointer"
  },
  ul: {
    color: " hsl(206, 100%, 42%)",
    borderBottom: "1px solid #ececec",
    margin: 0,
    padding: "5px 10px",
    cursor: "pointer",
    fontSize: 14,
    listStyleType: "none"
  },
  li: {
    marginTop: "2px",
    paddingRight: "10px",
    color: "gray",
    fontSize: "12px",
    fontWeight: 600
  },
  label: {
    padding: "10px",
    borderBottom: "1px solid #dfdfdf",
    background: "#f4f5f5",
    color: "#808080",
    "& > b": {
      display: "flex",
      alignItems: "center",
      "& svg": {
        color: "black"
      }
    }
  }
});

type RecentlySearchedItem = TopCustomerProps | TopProductProps | TopReportProps | TopPartnersProps;

const GetUserProfilePhoto = async (token: string) => {
  const response = await axios
    .create({
      headers: {
        Authorization: `Bearer ${token}`
      },
      responseType: "blob"
    })
    .get("https://graph.microsoft.com/v1.0/me/photos/48x48/$value");
  return window.URL.createObjectURL(response.data);
};

const LogoWithTitle = () => {
  const navigate = useNavigate();
  return (
    <>
      <img src={CxElogo} style={{ position: "absolute", left: 0, top: 0, padding: 6 }} width={35} alt="Security 360" />
      <span onClick={() => navigate("/")} className={overrideStyles.searchBar} tabIndex={0}>
        {" "}
        Security 360
      </span>
    </>
  );
};

/** Removes duplicate entries from recently searched items array.
 * - If `recentlySearchedItems` has items, it filters them to remove duplicates.
 * - If not provided, it defaults to an empty array.
 * @param recentlySearchedItems - Array of recently searched items( customers/products/reports) or undefined.
 * @returns A new array with unique entries based on Id values. */
const removeDuplicates = <T extends RecentlySearchedItem>(recentlSearchedItems: Array<T> | undefined): Array<T> => {
  if (!recentlSearchedItems) return [];
  const uniqueEntries = new Set<string>();
  const result = recentlSearchedItems.filter((each) => {
    let key: string | undefined;
    if ("tenantId" in each) {
      key = each.tenantId;
    } else if ("productId" in each) {
      key = each.productId;
    } else if ("reportId" in each) {
      key = each.reportId;
    } else if ("mpnId" in each) {
      key = each.mpnId;
    }
    if (!key) return false;
    if (uniqueEntries.has(key)) {
      return false;
    }
    uniqueEntries.add(key);
    return true;
  });
  return result;
};

const LoadSpinner = () => <Spinner styles={{ root: { height: 40 } }} size={SpinnerSize.small} label="Searching" />;
const LoadRecentSpinner = () => <Spinner styles={{ root: { height: 40 } }} size={SpinnerSize.small} label="Loading" />;

const HeaderComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [blobUrl, setBlobUrl] = useState("");
  const token = useAppSelector((state) => state.auth.graphToken);
  const userName = sessionStorage.getItem("loginName")?.toString();
  const userEmail = sessionStorage.getItem("loginUserName")?.toString();
  const [isFeedbackOpen, { setTrue: openFeedbackPanel, setFalse: dismissFeedbackPanel }] = useBoolean(false);
  const [isHelpOpen, { setTrue: openHelpPanel, setFalse: dismissHelpPanel }] = useBoolean(false);
  const [searchText, setSearchText] = useState("");
  const [allResultsVisible, setAllResultsVisible] = useState(false);
  const [showSearchList, setShowSearchList] = useState(true);
  const [showRecent, setShowRecent] = useState(false);
  const [rows, setRows] = useState<{ category: string; items: [] }[]>([]);
  const [placeholder, setPlaceholder] = useState("Search");
  const [reportModal, setReportModal] = useState<{
    active: boolean;
    data: any;
  }>({
    active: false,
    data: undefined
  });
  const searchBoxRef = useRef<ISearchBox>(null);
  const buttonStyles: IButtonStyles = { flexContainer: { width: "100%" } };
  const [insertRecentlySearchedCustomer] = useInsertRecentlySearchedCustomerMutation();
  const [insertRecentlySearchedProduct] = useInsertRecentlySearchedProductMutation();
  const [insertRecentlySearchedReport] = useInsertRecentlySearchedReportMutation();
  const userLoginDetails = useMemo(
    () => ({
      loginEmail: userEmail ?? "",
      loginName: userName ?? ""
    }),
    [userName, userEmail]
  );
  const { data, isFetching } = useGetRecentlySearchedItemsQuery(userLoginDetails, {
    skip: !showRecent || searchText.trim() !== ""
  });
  const recentRows = [
    { category: "Customers", items: removeDuplicates<TopCustomerProps>(data?.recentlySearchedCustomers) },
    { category: "Products", items: removeDuplicates<TopProductProps>(data?.recentlySearchedProducts) },
    { category: "Reports", items: removeDuplicates<TopReportProps>(data?.recentlySearchedReports) },
    { category: "Partners", items: removeDuplicates<TopPartnersProps>([]) }
  ];

  useEffect(() => {
    const rowgrpElements = document.querySelectorAll("[role=\"searchbox\"]");
    rowgrpElements.forEach(function (element) {
      element.removeAttribute("aria-expanded");
    });
    const roleElements = document.querySelectorAll("[role=\"combobox\"]");
    roleElements.forEach(function (element) {
      element.setAttribute("aria-expanded", "false");
    });
  }, [searchText]);

  const onReportModalClose = () => {
    setReportModal({ active: false, data: undefined });
  };

  /**
   * - Retrieves session storage key and search storage limit for given category
   * @param category - category type "Customers" | "Products" | "Reports"
   * @returns Object containing the storage key and limit, or undefined if not found.
   */
  const getLimitAndKey = (category: string): { value: string; limit: number } | undefined => {
    return GET_KEY_AND_SEARCH_LIMIT.find((each) => each.key === category);
  };

  /**
   * - Returns unique identifier (Id) for given item based on category.
   * @param row - Item object (customer/product/report)
   * @param category - category type "Customers" | "Products" | "Reports"
   * @returns corresponding Id as string
   */
  const getItemId = (row: RecentlySearchedItem, category: string): string => {
    switch (category) {
      case "Customers":
        return (row as TopCustomerProps).tenantId;
      case "Products":
        return (row as TopProductProps).productId;
      case "Reports": {
        const reportLink = (row as any)?.reportLink as string;
        if (reportLink) {
          const extractedReportId = reportLink.split("reports/")[1]?.split("/")[0] || "";
          return extractedReportId;
        }
        return (row as TopReportProps).reportId || "";
      }
      case "Partners":
        return (row as TopPartnersProps).mpnId || "";
      default:
        console.error("Unknown Category", category);
        return "";
    }
  };

  /** Adds a recently searched item to session storage if it's not already present.
   * - Fetches key and limit for the category using getLimitAndKey.
   * - Skips if key not found or item already exists.
   * - Updates session storage with new entry and maintains storage limit.
   * @param row - recently searched items( customer/product/report).
   * @param category - "Customers" | "Products" | "Reports".
   * @returns true if added , false otherwise */

  const checkDuplicates = <T extends RecentlySearchedItem>(row: T, category: string): boolean => {
    const searchedKey = getLimitAndKey(category);
    if (!searchedKey) return false;
    const { value, limit } = searchedKey;
    let searchedList: string[] = [];

    try {
      const checkExisting = sessionStorage.getItem(value);
      searchedList = checkExisting && isValidJson(checkExisting) ? JSON.parse(checkExisting) : [];
    } catch (e) {
      console.error(
        `Failed to parse recently searched ${category} from sessionStorage.Expected a JSON array of strings.`,
        e
      );
    }
    if (row) {
      const newEntry = getItemId(row, category);
      if (newEntry && !searchedList?.includes(newEntry)) {
        searchedList.unshift(newEntry);
        try {
          sessionStorage.setItem(value, JSON.stringify(searchedList));
          return true;
        } catch (e) {
          console.error(`Failed to save recently searched ${category} to sessionStorage`, e);
        }
      }
      if (searchedList.length > limit) {
        searchedList.pop();
      }
    }
    return false;
  };

  const handleClick = ({ row, category }: { row: any; category: string }) => {
    switch (category) {
      case "Customers":
        if (checkDuplicates<TopCustomerProps>(row, category)) {
          insertRecentlySearchedCustomer({
            LoginName: userName ?? "",
            LoginEmail: userEmail ?? "",
            TenantId: row?.tenantId,
            TenantName: row?.tenantName
          });
        }
        navigate("/customer?id=" + row.tenantId + "&from=search");
        break;
      case "Products":
        if (checkDuplicates<TopProductProps>(row, category)) {
          insertRecentlySearchedProduct({
            LoginName: userName ?? "",
            LoginEmail: userEmail ?? "",
            ProductId: row?.productId,
            ProductName: row?.productName
          });
        }
        navigate("/productview?productname=" + row.productName);
        break;
      case "Reports": {
        if (checkDuplicates<TopReportProps>(row, category)) {
          insertRecentlySearchedReport({
            LoginName: userName ?? "",
            LoginEmail: userEmail ?? "",
            ReportId: row?.reportLink?.split("reports/")[1]?.split("/")[0],
            Report: row?.report
          });
        }
        const reportId = row?.reportLink?.split("reports/")[1]?.split("/")[0] || row.reportId;
        // Check if the report is a Power BI report and does NOT contain 'apps'
        const isPowerBI = row?.reportLink && row.reportLink.includes("powerbi.com");
        const containsApps = row?.reportLink && row.reportLink.includes("apps");
        if (isPowerBI && !containsApps) {
          row?.isSupportReport
            ? navigate(`/supportreportview/reportid=${reportId}`, {
              state: { reportName: row.report, reportLink: row.reportLink, category: row.category }
            })
            : navigate(`/reportview/reportid=${reportId}`, {
              state: { reportName: row.report, reportLink: row.reportLink }
            });
        } else if (row?.reportLink) {
          window.open(row.reportLink, "_blank");
        }
        break;
      }
      case "Partners": {
        navigate(
          `/partners?partnerOneId=${encodeURIComponent(row.partnerOneID)}&mpnId=${encodeURIComponent(row.mpnId)}`
        );
        break;
      }
      default:
        break;
    }
    setShowSearchList(false);
    setShowRecent(false);
  };

  const onCustomRender = () => {
    const isSpinner = isFetching || !!(searchText.length && !allResultsVisible);

    const RenderCustomers = (gIndx: number, cIndx: number, row: any) => (
      <React.Fragment key={`RenderListFragment${gIndx}${cIndx}`}>
        {cIndx === 0 ? (
          <div className={overrideStyles.label}>
            <b>
              {showRecent ? (
                <>
                  <GroupsIcon /> &nbsp; Recently Searched Customers
                </>
              ) : (
                <>
                  <GroupsIcon />
                  &nbsp; Customers
                </>
              )}
            </b>
          </div>
        ) : (
          ""
        )}
        <ul
          key={`result-items-ul-customers-${gIndx + cIndx}`}
          className={`link-hover ${overrideStyles.ul}`}
          onPointerDown={() => handleClick({ row, category: "Customers" })}
          aria-hidden="true"
        >
          <b>{row.tenantName}</b>
          {showRecent ? (
            <></>
          ) : (
            <>
              <li key={`result-items-li-tpid-${gIndx + cIndx}`} className={overrideStyles.li}>
                TPID : {row.topParentId}
              </li>
              <li key={`result-items-li-tenantid-${gIndx + cIndx}`} className={overrideStyles.li}>
                Tenant ID : {row.tenantId}
              </li>
            </>
          )}
        </ul>
      </React.Fragment>
    );

    const RenderPartners = (gIndx: number, cIndx: number, row: any) => (
      <React.Fragment key={`RenderPartnersFragment${gIndx}${cIndx}`}>
        {cIndx === 0 ? (
          <div className={overrideStyles.label}>
            <b>
              {showRecent ? (
                <>
                  <GroupIcon /> &nbsp; Recently Searched Partners
                </>
              ) : (
                <>
                  <GroupIcon />
                  &nbsp; Partners
                </>
              )}
            </b>
          </div>
        ) : (
          ""
        )}
        <ul
          key={`result-items-ul-partners-${gIndx + cIndx}`}
          className={`link-hover ${overrideStyles.ul}`}
          onPointerDown={() => handleClick({ row, category: "Partners" })}
          aria-hidden="true"
        >
          <b>{row.mpnName}</b>
          {showRecent ? (
            <></>
          ) : (
            <>
              <li key={`result-items-li-partnerOneID-${gIndx + cIndx}`} className={overrideStyles.li}>
                PartnerOneID : {row.partnerOneID}
              </li>
              <li key={`result-items-li-mpnId-${gIndx + cIndx}`} className={overrideStyles.li}>
                Partner ID : {row.mpnId}
              </li>
            </>
          )}
        </ul>
      </React.Fragment>
    );

    const RenderProducts = (gIndx: number, cIndx: number, row: any) => (
      <React.Fragment key={`RenderProductsFragment${gIndx}${cIndx}`}>
        {cIndx === 0 ? (
          <div className={overrideStyles.label}>
            <b>
              {showRecent ? (
                <>
                  <CategoryIcon />
                  &nbsp; Recently Searched Products
                </>
              ) : (
                <>
                  <CategoryIcon />
                  &nbsp; Products
                </>
              )}
            </b>
          </div>
        ) : (
          ""
        )}
        <ul
          key={`result-items-ul-products-${gIndx + cIndx}`}
          className={`link-hover ${overrideStyles.ul}`}
          onPointerDown={() => handleClick({ row, category: "Products" })}
          aria-hidden="true"
        >
          <li key={`result-items-li-prodname-${gIndx + cIndx}`}>
            <b>{row.productName}</b>
          </li>
        </ul>
      </React.Fragment>
    );

    const RenderReports = (gIndx: number, cIndx: number, row: any) => (
      <React.Fragment key={`RenderReportsFragment${gIndx}${cIndx}`}>
        {cIndx === 0 ? (
          <div className={overrideStyles.label}>
            <b>
              {showRecent ? (
                <>
                  <AssessmentIcon />
                  &nbsp; Recently Searched Reports
                </>
              ) : (
                <>
                  <AssessmentIcon />
                  &nbsp; Reports
                </>
              )}
            </b>
          </div>
        ) : (
          ""
        )}
        <ul
          key={`result-items-ul-reports-${gIndx + cIndx}`}
          className={`link-hover ${overrideStyles.ul}`}
          onPointerDown={() => handleClick({ row, category: "Reports" })}
          aria-hidden="true"
        >
          <b>{row.report}</b>
          {showRecent ? (
            <></>
          ) : (
            <>
              {row.product && (
                <li key={`result-items-li-function-${gIndx + cIndx}`} className={overrideStyles.li}>
                  Product : {row.product}
                </li>
              )}
              {row.owner && (
                <li key={`result-items-li-product-${gIndx + cIndx}`} className={overrideStyles.li}>
                  Owner : {row.owner}
                </li>
              )}
            </>
          )}
        </ul>
      </React.Fragment>
    );

    const RenderDefault = (gIndx: number, cIndx: number) => (
      <React.Fragment key={`RenderDefaultFragment${gIndx}${cIndx}`}>
        {cIndx === 0 ? (
          <div className={overrideStyles.label}>
            <b>
              <span role="img" aria-label="info">
                ℹ️
              </span>
              &nbsp; No specific category found
            </b>
          </div>
        ) : (
          ""
        )}
        <ul
          key={`result-items-ul-default-${gIndx + cIndx}`}
          className={`link-hover ${overrideStyles.ul}`}
          aria-hidden="true"
          style={{ color: "#888" }}
        >
          <li key={`result-items-li-default-${gIndx + cIndx}`} className={overrideStyles.li}>
            No details available for this item.
          </li>
          <li key={`result-items-li-default-message-${gIndx + cIndx}`} className={overrideStyles.li}>
            Please select a valid category to see more information.
          </li>
        </ul>
      </React.Fragment>
    );
    const RenderList = () => {
      const navigate = useNavigate();
      const onShowMoreResults = () => {
        navigate("/search?q=" + searchText);
        setShowSearchList(false);
      };

      if (showRecent && !searchText.trim()) {
        return recentRows.map((group, gIndx) => (
          <React.Fragment key={`recentGroup-${gIndx}`}>
            {group.items.map((row: any, cIndx) => {
              switch (group.category) {
                case "Customers":
                  return RenderCustomers(gIndx, cIndx, row);
                case "Products":
                  return RenderProducts(gIndx, cIndx, row);
                case "Reports":
                  return RenderReports(gIndx, cIndx, row);
                case "Partners":
                  return RenderPartners(gIndx, cIndx, row);
                default:
                  return RenderDefault(gIndx, cIndx);
              }
            })}
          </React.Fragment>
        ));
      }

      if (rows?.length === 0) {
        return (
          <Stack
            horizontal
            tokens={{ childrenGap: 10, padding: 10 }}
            className={mergeStyles({
              alignItems: "center",
              justifyContent: "center",
              minHeight: 50
            })}
          >
            <Icon iconName="SearchIssue" title="No data found" />
            <span>No data found</span>
          </Stack>
        );
      }

      return rows?.map((group, gIndx, array) => {
        const last = gIndx + 1 === array.length;
        const style = mergeStyleSets({
          lastList: {
            color: "rgb(0, 90, 158)",
            cursor: "pointer",
            display: "flex",
            paddingLeft: "10px",
            alignItems: "center",
            height: "40px",
            width: "100%",
            userSelect: "text",
            "&:hover": {
              textDecoration: "none",
              background: "rgb(243, 242, 241)"
            }
          },
          searchIcon: {
            fontSize: "20px !important",
            fontWeight: 400,
            lineHeight: "20px",
            transform: "rotate(90deg)"
          }
        });
        return (
          <React.Fragment key={`RenderList${gIndx + 1}`}>
            {group.items.map((row: any, cIndx) => {
              switch (group.category) {
                case "Customers":
                  return RenderCustomers(gIndx, cIndx, row);
                case "Products":
                  return RenderProducts(gIndx, cIndx, row);
                case "Reports":
                  return RenderReports(gIndx, cIndx, row);
                case "Partners":
                  return RenderPartners(gIndx, cIndx, row);
                default:
                  return RenderDefault(gIndx, cIndx);
              }
            })}
            {last ? (
              <div key="showMoreResults" className={style.lastList} onClick={onShowMoreResults}>
                <SearchIcon className={style.searchIcon} />
                &nbsp;More search results
              </div>
            ) : (
              ""
            )}
          </React.Fragment>
        );
      });
    };

    const _onChange = (_event?: React.ChangeEvent<HTMLInputElement>, newSearchText?: string) => {
      setSearchText(newSearchText || "");
      setShowRecent(false);
    };

    const _onClear = () => {
      setSearchText("");
      setShowRecent(false);
    };

    const _onBlurHandler = () => {
      const input: HTMLInputElement | null = document.querySelector("#searchbox");

      if (!input?.value.trim()) setPlaceholder("Search");
      setShowRecent(false);
    };

    const _onFocusHandler = () => {
      if (!searchText.trim()) {
        setShowRecent(true);
        setShowSearchList(true);
        setPlaceholder("Look for customers, partners, products and reports.");
      }
    };

    return (
      <div>
        <LogoWithTitle />
        <EnhancedSearch
          minHighlightThreshold={1}
          onFocusHandler={_onFocusHandler}
          onBlurHandler={_onBlurHandler}
          onChange={_onChange}
          onClear={_onClear}
          onSearch={() => {
            searchBoxRef.current?.blur();
            setTimeout(() => searchBoxRef.current?.focus());
          }}
          value={searchText}
          placeholder={placeholder}
          styles={{
            subComponentStyles: {
              dropdownStyles: {
                root: { ".ms-Callout-main": { maxHeight: "325px !important" } }
              }
            }
          }}
          componentRef={searchBoxRef}
        >
          {isSpinner && <Template slot="result-items">{showRecent ? <LoadRecentSpinner /> : <LoadSpinner />}</Template>}
          <div slot="result-items">{showSearchList ? <RenderList /> : ""}</div>
        </EnhancedSearch>
      </div>
    );
  };

  const headerStyles = {
    subComponentStyles: {
      appNameLink: {
        root: {
          border: "none",
          height: "auto",
          lineHeight: "normal"
        }
      }
    },
    headerContainer: {
      position: "fixed",
      width: "100%"
    } as const
  };

  const searchSettings: IGlobalSearchProps & SearchSettings = {
    onSearch: () => { },
    onRender: onCustomRender
  };

  const panelStyles = {
    header: {
      paddingLeft: 18
    },
    main: {
      width: "500px"
    }
  };

  useEffect(() => {
    if (token) GetUserProfilePhoto(token).then(setBlobUrl);
  }, [token]);

  useEffect(() => {
    setRows([]);
    setAllResultsVisible(false);
    setShowSearchList(false);

    if (searchText.trim().length < 2) {
      setAllResultsVisible(true);
      return;
    }

    const timeout = setTimeout(() => {
      const promises: Promise<any>[] = [];
      const data: IAzSearchInput = {
        searchText: `${searchText}*`,
        size: 3
      };

      if (isNumberOnly(searchText)) {
        promises.push(
          getCustomers({
            ...data,
            searchText: "*",
            filter: applyAzSearchInputFilter("=", "TopParentId", searchText)
          })
        );
      } else if (isTenantIdOnly(searchText)) {
        promises.push(
          getCustomers({
            ...data,
            searchText: "*",
            filter: applyAzSearchInputFilter("=", "TenantId", `'${searchText.replaceAll("'", "\"")}'`)
          })
        );
      } else {
        data.searchText = `${searchText.replaceAll(" ", "+")}*`;
        promises.push(getCustomers({ ...data }));
      }
      promises.push(getProducts({ ...data }));

      promises.push(getReports({ ...data }));

      promises.push(getPartners({ ...data }));

      Promise.allSettled(promises)
        .then((values) => {
          const _rows = [];
          for (let idx = 0; idx < values.length; idx++) {
            const { status } = values[idx];
            if (status === "fulfilled") {
              const d = (values[idx] as PromiseFulfilledResult<any>).value;
              switch (idx) {
                case 0:
                  if (d.values.length) _rows.push({ category: "Customers", items: d.values });
                  break;
                case 1:
                  if (d.values.length) _rows.push({ category: "Products", items: d.values });
                  break;
                case 2:
                  if (d.values.length) _rows.push({ category: "Reports", items: d.values });
                  break;
                case 3:
                  if (d.values.length) _rows.push({ category: "Partners", items: d.values });
                  break;
                default:
                  break;
              }
            }
          }
          setAllResultsVisible(true);
          setShowSearchList(true);
          if (_rows.length) {
            setRows(_rows);
          }
        })
        .catch((error) => {
          console.error(error);
          setAllResultsVisible(true);
          setShowSearchList(false);
        });
    }, 1500);
    return () => clearTimeout(timeout);
  }, [searchText]);

  const profilePanelBody = (
    <AdminNavigationPanel currentPage={location.pathname.includes("cacheadmin") ? "cache-admin" : undefined} />
  );

  return (
    <>
      <div className={mergeStyles(headerStyles.headerContainer)}>
        <CoherenceHeader
          headerLabel={""}
          appNameSettings={{
            label: ""
          }}
          data-testid="mims-portal-header"
          searchSettings={searchSettings}
          farRightSettings={{
            profileSettings: {
              panelSettings: {
                fullName: sessionStorage.getItem("loginName")?.toString(),
                emailAddress: sessionStorage.getItem("loginUserName")?.toString(),
                imageUrl: blobUrl,
                logOutLink: "#",
                hideSignOut: true,
                hideSignOutDivider: true,
                hasCloseButton: true,
                forceFocusInsideTrap: true,
                closeButtonAriaLabel: "Close Profile panel",
                body: profilePanelBody
              }
            },

            additionalItems: [
              {
                key: "megaphone",
                iconProps: { iconName: "Megaphone" },
                title: "Announcements",
                text: "Announcements",
                ariaLabel: "Announcement",
                onClick: () => {
                  navigate("/announcement");
                },
                buttonStyles: buttonStyles
              },
              {
                key: "feedback",
                iconProps: { iconName: "Feedback" },
                title: "Feedback",
                text: "Feedback",
                ariaLabel: "Feedback",
                onClick: () => {
                  openFeedbackPanel();
                  return false;
                },
                buttonStyles: buttonStyles
              },
              {
                key: "help",
                iconProps: { iconName: "Help" },
                title: "Help",
                text: "Help",
                ariaLabel: "Help",
                onClick: () => {
                  openHelpPanel();
                  return false;
                },
                buttonStyles: buttonStyles
              }
            ]
          }}
          styles={headerStyles}
        />
        <Panel
          headerText="Feedback"
          isOpen={isFeedbackOpen}
          onDismiss={dismissFeedbackPanel}
          isLightDismiss
          closeButtonAriaLabel="Close feedback panel"
          styles={panelStyles}
        >
          <FeedbackBody />
        </Panel>
        <Panel
          headerText="Help"
          isOpen={isHelpOpen}
          onDismiss={dismissHelpPanel}
          isLightDismiss
          closeButtonAriaLabel="Close"
          styles={panelStyles}
        >
          <HeaderHelpBody />
        </Panel>
        {reportModal.active ? (
          <PowerBIReportModal
            ismodelOpen={reportModal.active}
            selectedItem={reportModal.data}
            CloseModalReoprt={onReportModalClose}
          />
        ) : (
          ""
        )}
      </div>
    </>
  );
};

export default HeaderComponent;
